import _ from 'lodash'
import Chart from 'chart'
import $ from 'jquery'
import prepareData from './chart-data/prepare-data'
import endpoint from 'impact-map-endpoint'
import constants from 'scoreboard/components/constants'
import globals from 'scoreboard/components/globals'
import { geographicTreeRootId } from 'scoreboard/charts/model/chart-designer'

function renderChartDesigner (options) {
	if (options.isSecondRender) {
		return prepareAndRender(options)
	}

	// #41664 We need to prevent multiple changes that will cause multiple
	// animations at once. For now, if we detect that a new series and breakdown
	// were added at the same time, we first need to render the breakdown, then render
	// the addition on the new serie(s)
	const originalDesigner = options.chartDesigner
	const originalData = options.chartData

	const oldDesigner = _.get(options.chartViewObject, 'lastChartDesigner')
	if (!oldDesigner) {
		// There is no previous render data; just proceed as normal
		return prepareAndRender(options)
	}

	const oldSerieCount = oldDesigner.getFeatures({ type: 'serie' }).length
	const newSerieCount = originalDesigner.getFeatures({ type: 'serie' }).length
	const oldBreakdownCount = oldDesigner.breakdowns.length
	const newBreakdownCount = originalDesigner.breakdowns.length
	if (!oldSerieCount || newSerieCount <= oldSerieCount || newBreakdownCount <= oldBreakdownCount) {
		// We either a. are adding our first serie b. are only adding a new breakdown
		// or c. only adding a new serie
		return prepareAndRender(options)
	}

	// Reset chart data to original series
	const newChartData = originalData.quickCopy()
	const oldChartData = _.get(options.chartViewObject, 'lastChartData')
	const plots = newChartData.plots()
	for (const plot of plots) {
		const oldPlot = oldChartData.plots().find(p => p.id == plot.id)
		if (oldPlot) {
			// Remove any series that didn't exist before
			const serieIds = _.map(oldPlot.series, s => s.id)
			plot.series = _.filter(plot.series, s => serieIds.includes(s.id))
		}
	}
	newChartData.plots(plots)

	// Reset new designer to old series and set chart data
	const newDesigner = originalDesigner.clone()
	newDesigner.features.set(oldDesigner.getFeatures())
	newDesigner.data = newChartData

	// Render the first animation
	options.chartDesigner = newDesigner
	options.chartData = newChartData
	const chart = prepareAndRender(options)

	// Setup the data for the second render so the caller can handle it appropriately
	options.chartDesigner = originalDesigner
	options.chartData = originalData

	if (chart) {
		chart.secondRenderData = options
	}

	return chart
}

/**
 * @param {Boolean} fresh #42036 if true, the old chart will be destroyed and a new one
 * 	rendered. This will skip animations; If false, the existing chart will be updated
 * 	and render animations
 */
function prepareAndRender({ chartDesigner, chartData, container, dimensions,
	chartViewObject, emphasize, fresh = false, zoomToIds }) {

	let lastChartData = _.get(chartViewObject, 'lastData')
	let lastChartDesigner = _.get(chartViewObject, 'lastChartDesigner')
	let data = prepareData({
		chart: chartDesigner,
		chartData,
		lastChartData
	})

	if (needFreshTreemap({ chartDesigner, currentData: data, previousData: lastChartData })) {
		fresh = true
	}

	return renderOnly({
		data,
		chartDesigner,
		container,
		chartViewObject,
		emphasize,
		doRender: ({ data, container }) => {

			let plotType = chartDesigner.get('plotType')
			if (chartDesigner.isShowingSingleChart()) {
				switch (plotType) {
					case 'pie': case 'donut': case 'timeline':
						container.closest('.chart-ratio-handler')
							.addClass('aspect-ratio-16-9 has-custom-aspect-ratio'); break
					case 'waffle': case 'filled-container': case 'funnel': case 'process':
						container.closest('.chart-ratio-handler')
							.addClass('aspect-ratio-3-4 has-custom-aspect-ratio'); break
					case 'pyramid': case 'cycle':
						container.closest('.chart-ratio-handler')
							.addClass('aspect-ratio-4-3 has-custom-aspect-ratio')
				}
			}

			// Add plot type class so we can target specific charts with CSS if needed
			container.addClass(`${plotType}-container`)

			let hasDimensions = !_.isNil(dimensions)
			let width = hasDimensions ? dimensions.width : container[0].offsetWidth
			let height = hasDimensions ? dimensions.height : container[0].offsetHeight
			// Set SVG height/width after we know if there's a custom aspect ratio
			data = _.extend({ width, height }, data)
			console.log(data)

			if (!chartViewObject) {
				chartViewObject = new Chart(container[0])
			}

			const isGeographicMap = plotType === 'filled-map' ||
				plotType == 'spike-map' || plotType == 'bubble-map'
			const renderChartViewObject = (newData) => {
				if (needFreshGeographicMap({
					currentDesigner: chartDesigner,
					previousDesigner: lastChartDesigner,
					currentData: data,
					previousData: lastChartData
				})) {
					fresh = true
				}

				// #49634 #49268 defer rendering and emitting 'rendered' until
				// the calling code has a chance to listen for the event.
				// This is particularly important for exports and dashboards
				process.nextTick(() => {
					chartViewObject.destroyTooltip()
					chartViewObject.render(newData, { fresh })
					chartViewObject.emit('rendered')
					chartViewObject.lastData = newData
					chartViewObject.lastChartData = chartData.quickCopy()
					chartViewObject.lastChartDesigner = chartDesigner.clone()

					const backEvent = isGeographicMap ? 'map:zoomTo' : 'back'
					$(container).find('.back-button-container')
						.off('click')
						.on('click', () => chartViewObject.emit(backEvent))
				})
			}

			if (isGeographicMap) {
				renderGeographicMap({
					data,
					chartData,
					chartDesigner,
					chart: chartViewObject,
					emphasize,
					zoomToIds,
					renderFn: (data) => renderChartViewObject(data)
				})
			} else {
				renderChartViewObject(data)
			}
			return chartViewObject
		}
	})
}

/**
 * Prepares the data for a geographic map and then calls the supplied render
 * callback
 */
function renderGeographicMap ({ data, chartData, chartDesigner, chart, zoomToIds,
	renderFn, emphasize }) {
	const serie = _.get(data, 'plots[0].series[0]')
	if (_.isNil(serie)) return

	const treeNodeById = chartData.treeItems()
	const rootId = geographicTreeRootId({ designer: chartDesigner })
	const initialDrilldownId = chartDesigner.get('startingViewTreeId') || rootId
	const treeId = !_.isNil(serie) && !!serie.drilldownId ?
		serie.drilldownId : initialDrilldownId

	let node = treeNodeById[treeId]
	node.children = _.filter(_.values(treeNodeById), n => n.parentId == node.id)
	let toponode = node

	let drilldown = chartDesigner.data.currentDrilldown()
	// let zoomTo = drilldown?.zoomTo
	let zoomTo
	if (zoomToIds) {
		// zoomToIds is passed in when a chart starts out already zoomed in,
		// like with exports, bookmarks, and briefings. In that case, use that
		// instead of whatever might be coming back from currentDrilldown()
		zoomTo = zoomToIds.map(id => chartDesigner.data.treeItems(id))
	} else {
		zoomTo = drilldown?.zoomTo
	}


	// #48574 In the event that a leaf node is clicked on, we are showing the
	// data from the layer above but showing the topojson of the leaf node
	const isLeafNode = !_.isNil(zoomTo) && _.size(zoomTo) == 1
	if (isLeafNode) {
		toponode = treeNodeById[zoomTo[0].id]
		zoomTo = []
	}

	fetchTopojson(rootId, toponode, chartDesigner, chart).then(topojson => {
		if (_.isEmpty(topojson)) return

		// set final axes.map settings, delete the back button node if there is
		// no parent
		const globe = !!chartDesigner.get('earth3DEnabled')
			&& toponode.type === 'earth' ? { globe: true } : {}
		_.extend(data.axes.map, { node: toponode, topojson, zoomTo }, globe)
		
		// if there is a zoomTo, keep the back button
		if (_.isNil(toponode.parentId) && _.isEmpty(zoomTo)) delete data.axes.map.back
		if (!_.isNil(emphasize)) data.axes.map.deemphasize = true

		preparePoints({
			data,
			node,
			treeNodeById,
			chartDesigner,
			emphasize
		})
		renderFn(data)
	})
}

/**
 * Sets the appropriate properties on all of the data points in a chart designer
 * map
 */
function preparePoints ({ data, node, treeNodeById, chartDesigner, emphasize }) {
	const pointsByTreeId = _.keyBy(data.plots[0].series[0].points, 'rangeId')
	const colorModel = chartDesigner.getConditionalColoringModel()

	// the chart repo expects no property at all if there are no children
	if (_.isEmpty(node.children)) delete node.children

	const nodes = _.isNil(node.children) ? [node] : node.children
	data.plots[0].series[0].points = _.map(nodes, c => {
		const point = pointsByTreeId[c.id]
		if (_.isNil(point)) {
			return { raw: undefined, nodeKey: c.key }
		} else {
			const treeNode = treeNodeById[point.rangeId]
			point.color = applyThresholdColoring(colorModel, point)
			point.deemphasize = !_.isNil(emphasize) && emphasize != point.color
			point.nodeKey = treeNode.key
			return point
		}
	})
}

/**
 * Fetches the topojson for the given node
 */
function fetchTopojson (rootId, node, designer, chart) {
	if (_.isNil(node)) {
		return Promise.resolve({})
	}

	const oldMap = chart?.lastData?.axes.map
	const sameNode = oldMap?.node.id == node.id
	const oldLevels = chart?.lastChartDesigner?.getEnabledMapLevels()
	const sameLevels = _.isEqual(oldLevels, designer.getEnabledMapLevels())

	// if we're viewing the same node and the same map levels are enabled, we
	// can just use the old data's topojson
	if (sameNode && sameLevels) {
		return Promise.resolve(oldMap.topojson)
	}

	const treeType = rootId === constants.SUBDIVISIONS_ROLLUP_ID ?
		'subdivisions' : 'postal-codes'
	const version = globals.mapDataVersion || ''
	return $.get({
		url: `resources/mapdata/data/topojson/${endpoint(treeType, node)}?${version}`
	}).then(rawData => {
		if (_.isNil(rawData)) return
		return JSON.parse(rawData)
	})
}

/**
 * Returns the correct hex color for a data point
 */
function applyThresholdColoring (colorModel, point) {
	if (_.isNil(point.raw)) return null
	const color = colorModel.color(point.raw, .5)
	return color.formatHex()
}

/**
 * Returns true if the treemap we're about to render needs to use the "fresh"
 * option (to avoid transition errors)
 */
function needFreshTreemap ({ chartDesigner, currentData, previousData }) {
	if (chartDesigner.get('plotType') !== 'tree-map') {
		return false
	}

	// #42218 describes a situation where a user has limited tree permissions,
	// and the final "back" action results in the treemap/table reverting to
	// its original state. This causes transition errors in the chart module,
	// since some higher-level items unexpectedly disappear.
	// We're going to detect that scenario, and just do a fresh render

	let currentSerie = _.get(currentData, 'plots[0].series[0]')
	if (!currentSerie) {
		return false
	}

	if (currentSerie.parentId) {
		// We're not at the root, so we should still be able to transition
		// properly
		return false
	}

	let previousSerie = _.get(previousData, 'plots[0].series[0]')
	if (!previousSerie) {
		return false
	}

	if (!previousSerie.parentId) {
		// This wasn't a "back" action if there wasn't previously a parentId
		return false
	}

	// If the current set of rangeIds doesn't have the previous serie's parentId,
	// then we hit the weird back situation in #42218. So don't try to transition;
	// just render a fresh chart
	let currentRangeIds = _.map(_.get(currentData, 'plots[0].breakdown.range'), r => r.id)
	return !_.includes(currentRangeIds, previousSerie.id)
}

/**
 * Returns true if the geographic map we're about to render needs to use the
 * "fresh" option (to avoid transition errors)
 */
function needFreshGeographicMap ({ currentDesigner, previousDesigner, currentData, previousData }) {
	const plotType = currentDesigner.get('plotType')
	if (!_.includes(['filled-map', 'bubble-map', 'spike-map'], plotType)) {
		return false
	}

	// if intially rendering
	const oldNode = previousData?.axes.map.node
	if (_.isNil(oldNode)) return false

	const newNode = currentData.axes.map.node
	const oldChildType = _.first(oldNode?.children)?.type
	const newChildType = _.first(newNode?.children)?.type

	const oldFieldId = previousDesigner?.datasetGeographicFieldId()
	const newFieldId = currentDesigner?.datasetGeographicFieldId()

	return (oldNode?.id == newNode?.id && oldChildType !== newChildType) ||
		oldFieldId != newFieldId
}

/**
 * Similar to renderChartDesigner(), but uses prepared data that's passed in,
 * instead of using prepareData()
 */
function renderOnly ({ data, container, chartViewObject, emphasize, doRender, chartDesigner }) {
	let isEmpty = _.isEmpty(data.plots) || _.isEmpty(data.plots[0].series)
		|| (chartDesigner && chartDesigner.hasError())
	// Remove the `.is-empty` class before getting scroll values to make sure
	// the min-height is correct
	container.toggleClass('is-empty', isEmpty)

	const isFilledMap = !!chartDesigner &&
		chartDesigner.get('plotType') === 'filled-map'
	if (!_.isNil(emphasize) && !isFilledMap) {
		data.plots.forEach(p => {
			p.series
				// de-emphasize each series that doesn't match emphasize ID
				.forEach(function (s) {
					const serieId = s.underlyingSerieId
					if ((typeof emphasize === 'number' || typeof emphasize === 'string') && serieId == emphasize) return
					if (emphasize.id && serieId == emphasize.id) return
					if (emphasize.tempId && serieId == emphasize.tempId) return

					s.deemphasize = true
				})
		})
	}

	if (isEmpty) {
		container.empty()
	} else {
		return doRender({ data, container })
	}
}

renderChartDesigner.renderOnly = renderOnly

export default renderChartDesigner

renderChartDesigner.__filename__ = 'charts/render-chart-designer.js'
